import styled from 'styled-components';

import type { PurchaseWebCampaignOffer } from '@xing-com/crate-common-graphql-types';
import { ProductType } from '@xing-com/crate-common-graphql-types';
import { useIsDesktop } from '@xing-com/crate-hooks-use-is-desktop';
import type { RefinedPremiumContacts } from '@xing-com/crate-upsell-types';
import { Divider } from '@xing-com/divider';
import { mediaWide, pagePaddingXL } from '@xing-com/layout-tokens';
import {
  space4XL,
  space5XL,
  spaceM,
  spaceXL,
  spaceXXL,
} from '@xing-com/tokens';
import { Column, GridContainer, Row } from '@xing-com/xing-grid';

import { EncryptionNotice, UpsellFooter } from '../components/footer';
import { NavigationRow } from '../components/navigation-row';
import { OfferBoxColumn } from '../components/offer-box/offer-box-column';
import { PremiumFeaturesOverview } from '../components/premium-features-overview';
import { ProjobsFeaturesOverview } from '../components/projobs-features-overview';
import { SingleColumnIllustrationAndSalutation } from '../components/single-column-illustration-and-salutation';
import { SmallScreenSalutationRow } from '../components/small-screen-salutation-row';

type CampaignPageProps = {
  product: ProductType;
  campaign?: PurchaseWebCampaignOffer | null | undefined;
  loading?: boolean;
  status?: string;
  premiumContactsLoading?: boolean;
  premiumContacts?: RefinedPremiumContacts | undefined;
  isPreview?: boolean;
  showFacepile?: boolean;
};

export const CampaignPage: React.FC<CampaignPageProps> = ({
  product,
  campaign,
  loading = true,
  status = 'LOADING',
  premiumContactsLoading = false,
  premiumContacts,
  isPreview = false,
  showFacepile = false,
}) => {
  const isDesktop = useIsDesktop();

  if (loading || premiumContactsLoading || !campaign) {
    return <></>;
  }
  if (status !== 'SUCCESS') {
    throw new Error('Upsell campaign could not be loaded');
  }
  const { textValues } = campaign;
  const { heading, legalNotice } = textValues;

  return (
    <>
      <Row data-qa="mainWrapper">
        <StyledNavigationRow disabled={isPreview} product={product} />
        <StyledSmallScreenSalutationRow
          heading={heading}
          premiumContacts={premiumContacts}
          showFacepile={showFacepile && !isDesktop}
        />
      </Row>
      <Row>
        <SingleColumnIllustrationAndSalutation
          heading={heading}
          product={product}
          premiumContacts={premiumContacts}
          showFacepile={showFacepile && !isDesktop}
        />
        <OfferBoxColumn
          product={product}
          showFacepile={showFacepile && isDesktop}
          premiumContacts={premiumContacts}
          disabled={isPreview}
          legalNotice={legalNotice}
          {...campaign}
        />
      </Row>
      <Row>
        <Column size={12} sizeWide={11}>
          <StyledFeaturesOverviewRow>
            {product === ProductType.Premium ? (
              <PremiumFeaturesOverview />
            ) : (
              <ProjobsFeaturesOverview />
            )}
          </StyledFeaturesOverviewRow>
          <footer>
            <>
              <AcrossDivider />
              <StyledGridContainer type="wide">
                <EncryptionNotice />
              </StyledGridContainer>
              <AcrossDivider />
              <StyledGridContainer type="wide">
                <UpsellFooter />
              </StyledGridContainer>
            </>
            <AcrossDivider />
          </footer>
        </Column>
      </Row>
    </>
  );
};

const StyledNavigationRow = styled(NavigationRow)`
  margin-bottom: ${spaceXL};
  padding-top: ${spaceM};
`;

const StyledSmallScreenSalutationRow = styled(SmallScreenSalutationRow)`
  margin-bottom: ${spaceXXL};

  @media ${mediaWide} {
    margin-bottom: 0;
  }
`;

const StyledGridContainer = styled(GridContainer)`
  @media ${mediaWide} {
    && {
      padding: 0 ${pagePaddingXL};
    }
  }
`;

const StyledFeaturesOverviewRow = styled.div`
  margin-top: ${space4XL};

  @media ${mediaWide} {
    margin-top: ${space5XL};
  }
`;

const AcrossDivider = styled(Divider)`
  position: absolute;
  left: 0;
  right: 0;
  width: 100vw;
  margin: 0 auto;
`;
